import { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useAuth } from "oidc-react";
import { useTranslation } from "react-i18next";

const SearchProductModual = ({ open, onClose, wono, setCounter }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const auth = useAuth();
  const accessToken = auth.userData?.access_token;
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;
  const preferred_username =
    auth.userData?.profile?.preferred_username?.toUpperCase();
  const [toastState, setToastState] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          `${rootUrl}WorkOrder/products/agreement/${wono}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch most products.");
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          console.warn(
            "Unexpected data format received from API. Expected an array."
          );
          setProducts([]);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchProducts();
  }, []);

  const saveProduct = () => {
    setIsProcessing(true);
    const woData = {
      wO_NO: wono,
      worK_ORDER_CODINGS: selectedProducts.map((product) => ({
        cataloG_NO: product.value,
        qtY_TO_INVOICE: quantity,
        qty: quantity,
        emP_NO: preferred_username,
        worK_ORDER_COST_TYPE: "E", //External
      })),
    };
    updateWorkOrder(woData);
  };

  const updateWorkOrder = async (woData) => {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
      method: "PATCH",
      headers: headers,
      body: JSON.stringify(woData),
    };
    let url = `${rootUrl}WorkOrder/wo`;
    const response = await fetch(url, options);

    if (response.ok) {
      setToastState({
        open: true,
        severity: "success",
        message: t("updateSuccess"),
      });
      setCounter(0);
    } else {
      setToastState({
        open: true,
        severity: "error",
        message: t("updateFail"),
      });
    }
    setIsProcessing(false);
  };

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 0.25);
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => Math.max(0, prev - 0.25));
  };

  const handleOptionSelect = (event, value) => {
    setSelectedProducts(value || []);
  };

  const isOptionEqualToValue = (option, value) => option.value === value.value;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
      <DialogTitle> {t("workOrder.materials.searchProduct.title")}</DialogTitle>
      <DialogContent>
        <div
          style={{ display: "flex", alignItems: "center", margin: "16px 0" }}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={products.map((product) => ({
              value: product.catalogNo,
              label: `${product.catalogNo} - ${product.catalogDesc}`,
            }))}
            getOptionLabel={(option) => option.label}
            id="controllable-states-demo"
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={checkboxIcon}
                  checkedIcon={checkboxCheckedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("workOrder.materials.subMaterials.selectSalePart")}
                fullWidth
              />
            )}
            value={selectedProducts}
            onChange={handleOptionSelect}
            isOptionEqualToValue={isOptionEqualToValue}
            fullWidth
          />
        </div>
        {/* Quantity Counter */}
        <div
          style={{ display: "flex", alignItems: "center", margin: "16px 0" }}
        >
          <IconButton onClick={decreaseQuantity}>
            <RemoveIcon />
          </IconButton>
          <TextField
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(0, +e.target.value))}
            style={{ width: "100px", textAlign: "center" }}
          />
          <IconButton onClick={increaseQuantity}>
            <AddIcon />
          </IconButton>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={saveProduct}
          color="primary"
          disabled={isProcessing || selectedProducts.length < 1}
        >
          {t("add")}
        </Button>
        <Button onClick={onClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Position and alignment
        style={{ position: "absolute", zIndex: 1301 }} // Adjust zIndex to be above other elements
      >
        <Alert
          severity={toastState.severity}
          variant="filled"
          onClose={() => setToastState({ ...toastState, open: false })}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SearchProductModual;
